import { Avatar, CloseButton, Modal } from '@pa/ui'
import { useId, useState } from 'react'
import type { Provider } from '../model/types'

/**
 * C-06a "Who can take {visit type}?" (UserFlows C-06a): All providers, or Only selected providers from a checklist
 * of every provider (hidden ones marked). None selected shows the warning; Done still works. Cancel, X and Escape
 * close without changes.
 */
export function WhoCanTakeModal({
  name,
  value,
  providers,
  onClose,
  onDone,
}: {
  name: string
  value: string[] | null
  providers: Provider[]
  onClose: () => void
  onDone: (ids: string[] | null) => void
}) {
  const titleId = useId()
  const [mode, setMode] = useState<'all' | 'some'>(value === null ? 'all' : 'some')
  const [ids, setIds] = useState<string[]>(value ?? providers.filter((p) => p.visible).map((p) => p.id))
  const toggle = (id: string) => {
    setIds((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]))
  }
  return (
    <Modal onClose={onClose} labelledBy={titleId}>
      <div className="modal-head">
        <div>
          <h2 id={titleId}>Who can take {name}?</h2>
          <p className="muted">Patients only see these providers for this visit type.</p>
        </div>
        <CloseButton onClick={onClose} />
      </div>
      <div className="modal-body who-can-take">
        <div className={`choice-row ${mode === 'all' ? 'on' : ''}`}>
          <input
            id={`${titleId}-all`}
            type="radio"
            className="radio"
            name="who-can-take"
            checked={mode === 'all'}
            onChange={() => {
              setMode('all')
            }}
          />
          <label className="grow" htmlFor={`${titleId}-all`}>
            <b>All providers</b>
            <small>Any visible provider can take this visit type</small>
          </label>
        </div>
        <div className={`choice-row ${mode === 'some' ? 'on' : ''}`}>
          <input
            id={`${titleId}-some`}
            type="radio"
            className="radio"
            name="who-can-take"
            checked={mode === 'some'}
            onChange={() => {
              setMode('some')
            }}
          />
          <label className="grow" htmlFor={`${titleId}-some`}>
            <b>Only selected providers</b>
            <small>Pick who can take it</small>
          </label>
        </div>
        {mode === 'some' && (
          <div className="check-list">
            {providers.map((p) => (
              <label key={p.id} className="check-row">
                <input
                  type="checkbox"
                  checked={ids.includes(p.id)}
                  onChange={() => {
                    toggle(p.id)
                  }}
                />
                <Avatar name={p.name} color={p.color} size="sm" />
                <span className="grow">
                  <b>{p.name}</b>
                  <small>
                    {p.specialty}
                    {!p.visible ? ' · hidden from patients' : ''}
                  </small>
                </span>
              </label>
            ))}
            {ids.length === 0 && (
              <div className="err-text" role="alert">
                Pick at least one provider, or patients won&apos;t see this visit type.
              </div>
            )}
          </div>
        )}
      </div>
      <div className="modal-foot">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            onDone(mode === 'all' ? null : ids)
          }}
        >
          Done
        </button>
      </div>
    </Modal>
  )
}
